import { Tooltip, Select, Box, Flex } from '@cowbell/ui';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCirclePause,
  faCircleExclamation,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faCircle,
  faClock,
  faHourglass,
} from '@fortawesome/pro-light-svg-icons';
import type { SelectOption } from '../../../types/components/common';
import { updateLabel } from '../../../api/cmail.api';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import { queries } from '../../../queries';
import { FilterType } from './useCmailState';
import classes from './LabelCell.module.css';

type LabelCellProps = {
  msgId: string;
  labelValue: string;
  options: SelectOption[];
};

export const LabelCell = ({ msgId, labelValue, options }: LabelCellProps) => {
  const handleAPIError = useAPIErrorHandler();
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const onChange = (value: string | null) => {
    if (!value) return;

    const payload = { label: value };

    updateLabel(msgId, { params: payload })
      .then(() => {
        notifications.show({
          title: `Updated label to ${value}`,
          type: 'success',
        });
      })
      .catch(handleAPIError(`Unable to update label, please try again`))
      .finally(() => {
        queryClient.invalidateQueries(queries.cmail.list._def);
      });
  };

  const getLabelIcon = (currentValue: string) => {
    if (
      !currentValue ||
      currentValue === FilterType.DEFAULT ||
      currentValue === 'OTHER'
    ) {
      return (
        <FontAwesomeIcon
          icon={faCircle}
          style={{ fontSize: '1.25rem', color: 'var(--mantine-color-dimmed)' }}
        />
      );
    }

    switch (currentValue) {
      case FilterType.COMPLETE:
        return (
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{
              fontSize: '1.25rem',
              color: 'var(--cmail-cowbell-green2)',
            }}
          />
        );
      case FilterType.INCOMPLETE:
        return (
          <FontAwesomeIcon
            icon={faClock}
            style={{
              fontSize: '1.25rem',
              color: 'var(--mantine-color-dimmed)',
            }}
          />
        );
      case FilterType.IN_PROGRESS:
        return (
          <FontAwesomeIcon
            icon={faHourglass}
            style={{
              fontSize: '1.25rem',
              color: 'var(--mantine-primary-color-filled)',
            }}
          />
        );
      case FilterType.ESCALATED:
        return (
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            style={{ fontSize: '1.25rem', color: 'var(--mantine-color-red-6)' }}
          />
        );
      case FilterType.ON_HOLD:
        return (
          <FontAwesomeIcon
            icon={faCirclePause}
            style={{
              fontSize: '1.25rem',
              color: 'var(--mantine-color-yellow-6)',
            }}
          />
        );
      case FilterType.FAILED:
        return (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            style={{ fontSize: '1.25rem', color: 'var(--mantine-color-red-6)' }}
          />
        );
      default:
        return (
          <FontAwesomeIcon
            icon={faCircle}
            style={{
              fontSize: '1.25rem',
              color: 'var(--mantine-color-dimmed)',
            }}
          />
        );
    }
  };

  const selectData = options.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  return (
    <Flex className={classes.selectWrapper}>
      <Tooltip label={labelValue || 'No label'} position="top">
        <Box>
          <Select
            className={classes.labelSelect}
            classNames={{ input: classes.labelSelectInput }}
            value={labelValue || ''}
            onChange={onChange}
            data={selectData}
            leftSection={getLabelIcon(labelValue)}
            leftSectionPointerEvents="none"
            variant="unstyled"
            allowDeselect={false}
            withCheckIcon={false}
            comboboxProps={{
              withinPortal: true,
              zIndex: 1400,
              width: 'auto',
              floatingStrategy: 'fixed',
            }}
            renderOption={({ option }) => (
              <Flex className={classes.optionRow}>
                {getLabelIcon(option.value)}
                <span>{option.label}</span>
              </Flex>
            )}
          />
        </Box>
      </Tooltip>
    </Flex>
  );
};
